import { createContext, ReactNode, useCallback, useEffect, useState } from 'react';
import { usePrevious } from 'react-use';

import useDebouncedState from '@app/hooks/useDebouncedState';

export const SearchFilterContext = createContext<{
  displayKeyword: string;
  limit: number;
  maxPage: number;
  page: number;
  searchKeyword: string;
  setLimit: (limit: number) => void;
  setMaxPage: (page: number) => void;
  setPage: (page: number) => void;
  setDisplayKeyword: (keyword: string) => void;
}>({
  displayKeyword: '',
  limit: 10,
  maxPage: 1,
  page: 1,
  searchKeyword: '',
  setLimit: () => {},
  setMaxPage: () => {},
  setPage: () => {},
  setDisplayKeyword: () => {},
});

export default function AddressBookSearchFilterProvider({ children }: { children: ReactNode }) {
  const [limit, setLimit] = useState<number>(10);
  const [maxPage, setMaxPage] = useState(1);
  const [page, setPage] = useState(1);
  const [searchKeyword, displayKeyword, setDisplayKeyword] = useDebouncedState('', 200);
  const prevSearchKeyword = usePrevious(searchKeyword);

  const handleSetPage = useCallback((maxPageValue: number) => {
    setPage(Math.max(maxPageValue, 1));
  }, []);

  const handleSetMaxPage = useCallback((maxPageValue: number) => {
    setMaxPage(Math.max(maxPageValue, 1));
  }, []);

  useEffect(() => {
    if (searchKeyword !== prevSearchKeyword) {
      handleSetPage(1);
    }
  }, [handleSetPage, prevSearchKeyword, searchKeyword]);

  useEffect(() => {
    if (page > maxPage) {
      handleSetPage(maxPage);
    }
  }, [handleSetPage, maxPage, page]);

  return (
    <SearchFilterContext.Provider
      value={{
        displayKeyword,
        limit,
        maxPage,
        page,
        searchKeyword,
        setLimit,
        setMaxPage: handleSetMaxPage,
        setPage: handleSetPage,
        setDisplayKeyword,
      }}
    >
      {children}
    </SearchFilterContext.Provider>
  );
}
